/* eslint-disable import/max-dependencies, no-unused-vars */
import type React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { serialize } from "object-to-formdata";

import {
  useHandleRequest,
  useIsAffiliate,
} from "@fas/cpa-state-manager/redux/hooks";
import type { UseEnterPressHook } from "@fas/cpa-state-manager/hooks";
import { useEnterPress } from "@fas/cpa-state-manager/hooks";
import type { ErrorsMapper } from "@fas/ui-framework";
import {
  setFormData,
  getFormData,
  addNotification,
  setErrors,
} from "@fas/ui-framework";
import { getUserInfo as getUserInfoSaga } from "@fas/cpa-state-manager/redux/actions/userInfo";
import { USER_INFO_FORM } from "../../constants";
import { getUserInfo, postUserInfo } from "../../services/deprecatedApi";

export type FetchError = {
  field: string;
  error?: string;
  message?: string;
};

export type UseUserInfoFormHook = {
  isLoading: boolean;
  disabled: boolean;
  onSave: () => unknown;
  onKeyPress: (arg0: React.KeyboardEvent) => unknown;
};
export const prepareFetchErrors: (arg0: FetchError[]) => ErrorsMapper = (
  errors
) =>
  errors.reduce(
    (errorsMapper: ErrorsMapper, error: FetchError): ErrorsMapper => {
      const prevMessage: string = (
        errorsMapper[error.field] || {
          message: "",
        }
      ).message;
      const currentMessage: string = error.error || error.message || "";
      const actualMessage: string = prevMessage
        ? prevMessage.concat(`\n${currentMessage}`)
        : currentMessage;
      return Object.assign(errorsMapper, {
        [error.field]: {
          message: actualMessage,
        },
      });
    },
    {}
  );
export const useUserInfoForm: () => UseUserInfoFormHook = () => {
  const dispatch = useDispatch();
  const { isLoading, changeLoading, onFail, onFinally } =
    useHandleRequest(USER_INFO_FORM);
  const form = useSelector((state: any) => getFormData(state, USER_INFO_FORM));
  useEffect(() => {
    changeLoading(true);
    getUserInfo()
      .then(({ data: { id, ...user } }) =>
        dispatch(setFormData(USER_INFO_FORM, user))
      ) // eslint-disable-line
      .catch(onFail)
      .finally(onFinally);
  }, []);

  const onSave = () => {
    changeLoading(true);
    const { howFoundOutComment, howFoundOut, email, photo, ...body } = form;

    if (photo instanceof File || photo === null) {
      body.photo = photo;
    }

    postUserInfo(
      serialize(body, {
        allowEmptyArrays: true,
      })
    )
      .then(() => {
        dispatch(
          addNotification({
            severity: "success",
            message: "Personal info updated successfully",
          })
        );
        dispatch(getUserInfoSaga());
      })
      .catch(async (err: any) => {
        await onFail(err);

        if (
          err.isAxiosError &&
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          Array.isArray(err.response.data.errors)
        ) {
          dispatch(setErrors(prepareFetchErrors(err.response.data.errors)));
        }
      })
      .finally(onFinally);
  };

  const { onKeyPress }: UseEnterPressHook = useEnterPress(onSave);
  return {
    isLoading,
    disabled: !useIsAffiliate(),
    onSave,
    onKeyPress,
  };
};
